import { useState, useCallback, useMemo } from 'react'
import { Flex, Text, TextField, Select, IconButton } from '@radix-ui/themes'
import { ChevronLeftIcon, ChevronRightIcon, TrashIcon } from '@radix-ui/react-icons'
import { formatBarcode, BarcodeFormat, BarcodeFormatsConfig } from './claim-form-constants'

type TicketEntry = {
  prizeAmount: unknown
  gameType: string
  barcodeNumber: string
}

type EditableTicketFieldsProps = {
  data: Record<string, unknown>
  schema: Record<string, unknown>
  formData?: Record<string, unknown>
  onChange: (updated: Record<string, unknown>) => void
  currentIndex?: number
  onIndexChange?: (index: number) => void
}

export function EditableTicketFields({
  data,
  schema,
  formData,
  onChange,
  currentIndex: controlledIndex,
  onIndexChange,
}: EditableTicketFieldsProps) {
  const tickets = useMemo(() => (data as unknown as TicketEntry[]) ?? [], [data])
  const paymentMethod = (formData?.paymentMethod ?? '') as string
  const [internalIndex, setInternalIndex] = useState(controlledIndex ?? 0)
  const currentIndex = controlledIndex ?? internalIndex
  const [jumpInput, setJumpInput] = useState(String(currentIndex + 1))
  const [formatIndices, setFormatIndices] = useState<Record<number, number>>({})
  const total = tickets.length

  const sectionSchema = schema?.properties as Record<string, unknown> | undefined
  const ticketsSchema = sectionSchema?.tickets as Record<string, unknown> | undefined
  const ticketItemsSchema = ticketsSchema?.items as Record<string, unknown> | undefined
  const ticketProperties = (ticketItemsSchema?.properties ?? {}) as Record<string, unknown>
  const ticketRequired = ((ticketItemsSchema?.required ?? []) as string[]) || []

  const topLevelProps = sectionSchema ?? {}
  const paymentMethodSchema = topLevelProps.paymentMethod as Record<string, unknown> | undefined
  const paymentMethodOptions: string[] = (paymentMethodSchema?.enum as string[]) ?? []
  const hasPaymentMethod = paymentMethodSchema !== undefined

  const gameTypeSchema = ticketProperties.gameType as Record<string, unknown> | undefined
  const gameTypeOptions: string[] = (gameTypeSchema?.enum as string[]) ?? []

  const hasGameType = 'gameType' in ticketProperties
  const hasPrizeAmount = 'prizeAmount' in ticketProperties
  const hasBarcodeNumber = 'barcodeNumber' in ticketProperties

  const barcodeFormats = (schema?.barcodeFormats ?? {}) as BarcodeFormatsConfig

  const isMultiFormat = (gameType: string) => Array.isArray(barcodeFormats[gameType])

  const getSingleFormat = (gameType: string): BarcodeFormat | undefined => {
    const fmt = barcodeFormats[gameType]
    if (!fmt || Array.isArray(fmt)) {
      return undefined
    }
    return fmt
  }

  const isGameTypeRequired = ticketRequired.includes('gameType')
  const isPrizeAmountRequired = ticketRequired.includes('prizeAmount')
  const isBarcodeRequired = ticketRequired.includes('barcodeNumber')

  const ticket = useMemo(
    () => (tickets[currentIndex] as Record<string, unknown>) ?? {},
    [tickets, currentIndex],
  )

  const selectedFormatIndex = formatIndices[currentIndex] ?? 0

  const goTo = useCallback(
    (index: number) => {
      const clamped = Math.max(0, Math.min(total - 1, index))
      if (onIndexChange) {
        onIndexChange(clamped)
      } else {
        setInternalIndex(clamped)
      }
      setJumpInput(String(clamped + 1))
    },
    [total, onIndexChange],
  )

  const updateTicketField = useCallback(
    (field: string, value: unknown) => {
      const updatedTickets = [...tickets] as Record<string, unknown>[]
      updatedTickets[currentIndex] = { ...updatedTickets[currentIndex], [field]: value }
      onChange(updatedTickets as unknown as Record<string, unknown>)
    },
    [tickets, currentIndex, onChange],
  )

  const updateTicketFields = useCallback(
    (fields: Record<string, unknown>) => {
      const updatedTickets = [...tickets] as Record<string, unknown>[]
      updatedTickets[currentIndex] = { ...updatedTickets[currentIndex], ...fields }
      onChange(updatedTickets as unknown as Record<string, unknown>)
    },
    [tickets, currentIndex, onChange],
  )

  const updatePaymentMethod = useCallback(
    (value: string) => {
      // Payment method lives at the form level, not in tickets data.
      // We signal this upward; the parent handles merging it into formData.
      // For now, store it alongside tickets in the editing data as a workaround.
      // The save handler in the drawer will need to handle this.
      onChange({
        ...data,
        _paymentMethod: value,
      } as unknown as Record<string, unknown>)
    },
    [data, onChange],
  )

  const handleTicketCountChange = useCallback(
    (newCount: number) => {
      if (Number.isNaN(newCount) || newCount < 1) {
        return
      }
      if (newCount > total) {
        const blank: TicketEntry = { prizeAmount: 0, gameType: '', barcodeNumber: '' }
        const added = Array.from({ length: newCount - total }, () => ({ ...blank }))
        const updated = [...tickets, ...added] as unknown as Record<string, unknown>
        onChange(updated)
        const newIndex = newCount - 1
        if (onIndexChange) {
          onIndexChange(newIndex)
        } else {
          setInternalIndex(newIndex)
        }
        setJumpInput(String(newIndex + 1))
      } else if (newCount < total) {
        const updated = tickets.slice(0, newCount) as unknown as Record<string, unknown>
        onChange(updated)
        const clampedIndex = Math.min(currentIndex, newCount - 1)
        if (onIndexChange) {
          onIndexChange(clampedIndex)
        } else {
          setInternalIndex(clampedIndex)
        }
        setJumpInput(String(clampedIndex + 1))
      }
    },
    [tickets, total, currentIndex, onChange, onIndexChange],
  )

  const removeTicket = useCallback(() => {
    const updated = tickets.filter((_, i) => i !== currentIndex) as unknown as Record<
      string,
      unknown
    >
    onChange(updated)
    setFormatIndices((prev) => {
      const next: Record<number, number> = {}
      Object.entries(prev).forEach(([k, v]) => {
        const ki = Number(k)
        if (ki < currentIndex) {
          next[ki] = v
        } else if (ki > currentIndex) {
          next[ki - 1] = v
        }
      })
      return next
    })
    goTo(Math.min(currentIndex, tickets.length - 2))
  }, [tickets, currentIndex, onChange, goTo])

  const gameType = (ticket.gameType as string | undefined) ?? ''
  const multiFormats = isMultiFormat(gameType)
    ? (barcodeFormats[gameType] as BarcodeFormat[])
    : null

  return (
    <Flex direction="column" gap="3">
      <div className="claim-form-two-col claim-form-ticket-summary">
        <Flex direction="column" gap="1" className="claim-form-field">
          <Text size="1" color="gray">
            Number of Tickets
          </Text>
          <TextField.Root
            size="1"
            type="number"
            style={{ width: '64px' }}
            value={total}
            min={1}
            onChange={(e) => handleTicketCountChange(Number.parseInt(e.target.value, 10))}
          />
        </Flex>
        {hasPaymentMethod && (
          <Flex direction="column" gap="1" className="claim-form-field">
            <Text size="1" color="gray">
              Payment Method
            </Text>
            {paymentMethodOptions.length > 0 ? (
              <Select.Root size="1" value={paymentMethod} onValueChange={updatePaymentMethod}>
                <Select.Trigger placeholder="Select..." />
                <Select.Content>
                  {paymentMethodOptions.map((opt) => (
                    <Select.Item key={opt} value={opt}>
                      {opt}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            ) : (
              <TextField.Root
                size="1"
                value={paymentMethod}
                onChange={(e) => updatePaymentMethod(e.target.value)}
              />
            )}
          </Flex>
        )}
      </div>

      <Flex align="center" gap="2">
        <Text size="2" weight="bold">
          Ticket {currentIndex + 1} of {total}
        </Text>
        {total > 1 && (
          <IconButton
            size="1"
            variant="ghost"
            color="red"
            onClick={removeTicket}
            aria-label="Delete ticket">
            <TrashIcon />
          </IconButton>
        )}
      </Flex>

      <div className="claim-form-three-col">
        {hasPrizeAmount && (
          <Flex direction="column" gap="1" className="claim-form-field">
            <Text size="1" color="gray">
              Prize Amount {isPrizeAmountRequired && '*'}
            </Text>
            <TextField.Root
              size="1"
              type="text"
              inputMode="decimal"
              value={(ticket.prizeAmount as string | number | undefined)?.toString() ?? ''}
              onChange={(e) => {
                const cleaned = e.target.value.replaceAll(/[^0-9.]/g, '')
                updateTicketField('prizeAmount', cleaned === '' ? undefined : Number(cleaned))
              }}
              placeholder="600">
              <TextField.Slot>$</TextField.Slot>
            </TextField.Root>
          </Flex>
        )}
        {hasGameType && (
          <Flex direction="column" gap="1" className="claim-form-field">
            <Text size="1" color="gray">
              Game Type {isGameTypeRequired && '*'}
            </Text>
            {gameTypeOptions.length > 0 ? (
              <Select.Root
                size="1"
                value={gameType}
                onValueChange={(v) => {
                  updateTicketFields({ gameType: v, barcodeNumber: '' })
                  setFormatIndices((prev) => ({ ...prev, [currentIndex]: 0 }))
                }}>
                <Select.Trigger placeholder="Select..." />
                <Select.Content>
                  {gameTypeOptions.map((opt) => (
                    <Select.Item key={opt} value={opt}>
                      {opt}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            ) : (
              <TextField.Root
                size="1"
                value={gameType}
                onChange={(e) => {
                  updateTicketFields({ gameType: e.target.value, barcodeNumber: '' })
                  setFormatIndices((prev) => ({ ...prev, [currentIndex]: 0 }))
                }}
              />
            )}
          </Flex>
        )}
        {hasBarcodeNumber && (!hasGameType || !!ticket.gameType) && (
          <Flex direction="column" gap="1" className="claim-form-field">
            <Text size="1" color="gray">
              Barcode Number {isBarcodeRequired && '*'}
            </Text>
            {multiFormats ? (
              <Flex direction="column" gap="1">
                {multiFormats.map((fmt, fIdx) => {
                  const isSelected = selectedFormatIndex === fIdx
                  return (
                    <Flex key={fIdx} align="center" gap="2">
                      <input
                        type="radio"
                        name={`barcode-format-${currentIndex}`}
                        checked={isSelected}
                        onChange={() => {
                          setFormatIndices((prev) => ({ ...prev, [currentIndex]: fIdx }))
                          updateTicketField('barcodeNumber', '')
                        }}
                      />
                      {isSelected ? (
                        <TextField.Root
                          size="1"
                          style={{ flex: 1 }}
                          value={(ticket.barcodeNumber as string | undefined) ?? ''}
                          placeholder={fmt.placeholder ?? 'Enter barcode'}
                          maxLength={fmt.placeholder?.length}
                          onChange={(e) => {
                            const formatted = fmt.placeholder
                              ? formatBarcode(e.target.value, fmt.placeholder)
                              : e.target.value
                            updateTicketField('barcodeNumber', formatted)
                          }}
                        />
                      ) : (
                        <Text size="1" color="gray">
                          {fmt.placeholder}
                        </Text>
                      )}
                    </Flex>
                  )
                })}
              </Flex>
            ) : (
              <TextField.Root
                size="1"
                value={String((ticket.barcodeNumber as string) ?? '')}
                onChange={(e) => {
                  const format = getSingleFormat(gameType)
                  const formatted = format?.placeholder
                    ? formatBarcode(e.target.value, format.placeholder)
                    : e.target.value
                  updateTicketField('barcodeNumber', formatted)
                }}
                maxLength={getSingleFormat(gameType)?.placeholder?.length}
                placeholder={getSingleFormat(gameType)?.placeholder ?? 'Enter barcode'}
              />
            )}
          </Flex>
        )}
      </div>

      {total > 1 && (
        <Flex direction="column" align="center" gap="1" className="claim-form-ticket-nav">
          <Text size="1" color="gray">
            Jump to Ticket
          </Text>
          <Flex align="center" gap="2">
            <IconButton
              size="1"
              variant="soft"
              color="gray"
              disabled={currentIndex === 0}
              onClick={() => goTo(currentIndex - 1)}>
              <ChevronLeftIcon />
            </IconButton>
            <input
              className="claim-form-ticket-jump-input"
              value={jumpInput}
              onChange={(e) => setJumpInput(e.target.value)}
              onBlur={() => {
                const num = Number.parseInt(jumpInput, 10)
                if (Number.isNaN(num)) {
                  setJumpInput(String(currentIndex + 1))
                } else {
                  goTo(num - 1)
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.currentTarget.blur()
                }
              }}
            />
            <Text size="1" color="gray">
              of {total}
            </Text>
            <IconButton
              size="1"
              variant="soft"
              color="gray"
              disabled={currentIndex === total - 1}
              onClick={() => goTo(currentIndex + 1)}>
              <ChevronRightIcon />
            </IconButton>
          </Flex>
        </Flex>
      )}
    </Flex>
  )
}
